import { Check, X, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TriageResultDto } from '@/stores/useConnectorTriageStore';
import { cn } from '@/lib/utils';

const DOC_TYPES = [
  'Protocol',
  'Manual',
  'Certificate',
  'Drawing',
  'Report',
  'Standard',
  'Contract',
];

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100);
  const variant = confidence >= 0.8 ? 'default' : confidence >= 0.5 ? 'secondary' : 'destructive';
  return (
    <Badge variant={variant} className="text-xs tabular-nums">
      {pct}%
    </Badge>
  );
}

interface TriageResultRowProps {
  result: TriageResultDto;
  selected: boolean;
  onSelect: (docId: string, checked: boolean) => void;
  onUpdateField: (docId: string, field: 'doc_type' | 'model_name', value: string) => void;
  onUpdateStatus: (docId: string, status: 'confirmed' | 'rejected') => void;
}

export function TriageResultRow({
  result,
  selected,
  onSelect,
  onUpdateField,
  onUpdateStatus,
}: TriageResultRowProps) {
  const [editingDocType, setEditingDocType] = useState(false);
  const [editingModel, setEditingModel] = useState(false);
  const [localModel, setLocalModel] = useState(result.machine_metadata?.model_name?.value || '');

  const isConfirmed = result.triage_status === 'confirmed';
  const isRejected = result.triage_status === 'rejected';

  return (
    <tr
      className={cn(
        'border-b transition-colors',
        isConfirmed && 'bg-green-50 dark:bg-green-950/20',
        isRejected && 'bg-red-50 dark:bg-red-950/20 opacity-60',
      )}
    >
      {/* Checkbox */}
      <td className="px-3 py-2">
        <Checkbox
          checked={selected}
          onCheckedChange={(checked) => onSelect(result.doc_id, !!checked)}
        />
      </td>

      {/* Filename */}
      <td className="px-3 py-2 text-sm font-medium max-w-[200px] truncate" title={result.file_name}>
        {result.file_name}
      </td>

      {/* Document Type */}
      <td className="px-3 py-2 text-sm">
        {editingDocType ? (
          <Select
            value={result.doc_type || ''}
            onValueChange={(val) => {
              onUpdateField(result.doc_id, 'doc_type', val);
              setEditingDocType(false);
            }}
          >
            <SelectTrigger className="h-7 w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span
            className="cursor-pointer hover:underline inline-flex items-center gap-1"
            onClick={() => setEditingDocType(true)}
          >
            {result.doc_type || '—'}
            <Pencil className="h-3 w-3 text-muted-foreground" />
          </span>
        )}
      </td>

      {/* Doc Type Confidence */}
      <td className="px-3 py-2">
        <ConfidenceBadge confidence={result.doc_type_confidence} />
      </td>

      {/* Machine Model */}
      <td className="px-3 py-2 text-sm">
        {editingModel ? (
          <div className="flex items-center gap-1">
            <Input
              className="h-7 w-[140px] text-sm"
              value={localModel}
              onChange={(e) => setLocalModel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onUpdateField(result.doc_id, 'model_name', localModel);
                  setEditingModel(false);
                }
                if (e.key === 'Escape') setEditingModel(false);
              }}
              autoFocus
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => {
                onUpdateField(result.doc_id, 'model_name', localModel);
                setEditingModel(false);
              }}
            >
              <Check className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <span
            className="cursor-pointer hover:underline inline-flex items-center gap-1"
            onClick={() => setEditingModel(true)}
          >
            {result.machine_metadata?.model_name?.value || '—'}
            <Pencil className="h-3 w-3 text-muted-foreground" />
          </span>
        )}
      </td>

      {/* Model Confidence */}
      <td className="px-3 py-2">
        {result.machine_metadata?.model_name?.value ? (
          <ConfidenceBadge confidence={result.machine_metadata.model_name.confidence} />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>

      {/* Overall Confidence */}
      <td className="px-3 py-2">
        <ConfidenceBadge confidence={result.overall_confidence} />
      </td>

      {/* Actions */}
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant={isConfirmed ? 'default' : 'ghost'}
            className="h-6 w-6"
            onClick={() => onUpdateStatus(result.doc_id, 'confirmed')}
            title="Bestätigen"
          >
            <Check className="h-3 w-3" />
          </Button>
          <Button
            size="icon"
            variant={isRejected ? 'destructive' : 'ghost'}
            className="h-6 w-6"
            onClick={() => onUpdateStatus(result.doc_id, 'rejected')}
            title="Ablehnen"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </td>
    </tr>
  );
}
